import { useState } from "react";
import { IconDownload, IconShare } from "./icons";
import {
  downloadRecording,
  shareRecording,
  type DownloadProgress,
  type RecordingExportSpec,
} from "../lib/recording-export";

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecordingExportButtons({
  spec,
  ready,
}: {
  spec: RecordingExportSpec;
  ready: boolean;
}) {
  const [busy, setBusy] = useState<"download" | "share" | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (kind: "download" | "share") => {
    if (busy) return;
    setBusy(kind);
    setError(null);
    setMessage(null);
    setPercent(kind === "download" ? 0 : null);
    setProgress(kind === "download" ? "Downloading 0%" : null);
    const onProgress = (row: DownloadProgress) => {
      setPercent(row.percent);
      const size = row.totalBytes
        ? `${formatBytes(row.receivedBytes)} / ${formatBytes(row.totalBytes)}`
        : formatBytes(row.receivedBytes);
      const speed = row.speedBps && row.speedBps > 0 ? ` · ${formatBytes(row.speedBps)}/s` : "";
      setProgress(`Downloading ${row.percent}% · ${size}${speed}`);
    };
    const work =
      kind === "download"
        ? downloadRecording(spec, onProgress).then(() => {
            setPercent(100);
            setMessage("Saved.");
          })
        : shareRecording(spec).then((result) => {
            if (result.shared) setMessage("Share sheet opened.");
            else if (result.copied) setMessage("Video link copied.");
          });
    void work
      .catch((err) => setError(err instanceof Error ? err.message : "Could not prepare this recording."))
      .finally(() => {
        setBusy(null);
        setProgress(null);
        setPercent(null);
      });
  };

  return (
    <div className="video-export" onClick={(e) => e.stopPropagation()}>
      <div className="video-export-actions">
        <button
          type="button"
          className="video-export-btn"
          disabled={!ready || busy != null}
          title={ready ? `Download ${spec.cameraLabel}` : "Wait until this recording is playing"}
          aria-label={`Download ${spec.cameraLabel} recording`}
          onClick={() => run("download")}
        >
          <IconDownload />
          <span>{busy === "download" ? `${percent ?? 0}%` : "Download"}</span>
        </button>
        <button
          type="button"
          className="video-export-btn"
          disabled={!ready || busy != null}
          title={ready ? `Share ${spec.cameraLabel}` : "Wait until this recording is playing"}
          aria-label={`Share ${spec.cameraLabel} recording`}
          onClick={() => run("share")}
        >
          <IconShare />
          <span>{busy === "share" ? "Sharing…" : "Share"}</span>
        </button>
      </div>
      {progress ? (
        <p className="video-export-status">
          {progress}
          {percent != null ? (
            <span className="video-export-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
              <span style={{ width: `${percent}%` }} />
            </span>
          ) : null}
        </p>
      ) : null}
      {message && !progress ? <p className="video-export-status is-ok">{message}</p> : null}
      {error ? <p className="video-export-status is-error">{error}</p> : null}
    </div>
  );
}
